import { type ReactNode, useEffect, useState } from "react";
import { Provider } from "react-redux";
import { Route } from "react-router-dom";
import type { TripsModuleProps } from "@hopper-b2b/types";
import { Slot } from "@hopper-b2b/ui";
import {
  FeatureFlagsContext,
  type FeatureFlagsContextProps,
  UserSourceProvider,
  useDeviceTypes,
  useWindowEventListener,
} from "@hopper-b2b/utilities";
import {
  StylesProvider,
  ThemeProvider,
  createGenerateClassName,
} from "@material-ui/core";
import { ClientContext } from "./ClientContext";
import { TripsList } from "./pages/TripsList";
import { UniversalDisruptionExercise } from "./pages/UberTripsList/components/UniversalDisruptionExercise/UniversalDisruptionExercise";
import { store } from "./store";
import { eventsToListen } from "./utils/events";
import { PATH_HOME, PATH_DISRUPTION_EXERCISE } from "./utils/paths";

import "./App.scss";

const generateClassName = createGenerateClassName({
  productionPrefix: "ptTripsModule",
  seed: "ptTripsModule",
});

export { ClientContext };

type TripsAppProps = TripsModuleProps & {
  featureFlags?: FeatureFlagsContextProps;
  children?: ReactNode;
};

const App = ({
  clientAssets,
  isAgentPortal,
  theme,
  apiConfig,
  featureFlags,
  onSupportClick,
  children,
  onBack,
  onGoToMyTrips,
}: TripsAppProps) => {
  const { matchesMobile } = useDeviceTypes();

  const [activeTheme, setActiveTheme] = useState(theme);
  const [sessionInfo] = useState(clientAssets.sessionInfo);

  useEffect(() => {
    document.title = "My Trips and Support";
    return () => {
      document.title = "Travel Portal";
    };
  }, []);

  const handleThemeChanged = (e: CustomEvent) => {
    setActiveTheme(e.detail);
  };
  useWindowEventListener(eventsToListen.HOST_THEME_CHANGED, handleThemeChanged);
  return (
    <Provider store={store}>
      <UserSourceProvider>
        <ClientContext.Provider
          value={{ ...clientAssets, isAgentPortal, sessionInfo }}
        >
          <FeatureFlagsContext.Provider value={featureFlags ?? {}}>
            <div className="App">
              <StylesProvider generateClassName={generateClassName}>
                <ThemeProvider theme={activeTheme}>
                  {children}
                  <Route path={PATH_HOME} exact>
                    <Slot
                      id="mytrips-list"
                      isMobile={matchesMobile}
                      apiConfig={apiConfig}
                      onSupportClick={onSupportClick}
                      goBack={onBack}
                      onGoToMyTrips={onGoToMyTrips}
                      component={
                        <TripsList
                          isMobile={matchesMobile}
                          apiConfig={apiConfig}
                          onSupportClick={onSupportClick}
                        />
                      }
                    />
                  </Route>
                  {/* TODO: Will remove later once Uber is fully integrated with core (https://hopper-jira.atlassian.net/browse/DS-992) */}
                  <Route path={PATH_DISRUPTION_EXERCISE}>
                    <UniversalDisruptionExercise />
                  </Route>
                </ThemeProvider>
              </StylesProvider>
            </div>
          </FeatureFlagsContext.Provider>
        </ClientContext.Provider>
      </UserSourceProvider>
    </Provider>
  );
};

export default App;
